// src/redux/store.ts
import {configureStore} from '@reduxjs/toolkit';
import {combineReducers} from 'redux';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import addExportSlice from './slices/addExportSlice';
import alertSlice from './slices/alertSlice';
import analyticsSlice from './slices/analyticsSlice';
import askLioSlice from './slices/askLioSlice';
import budgetTrackerModalSlice from './slices/budgetTrackerSlice';
import bulkUserEditSlice from './slices/bulkUserEditSlice';
import bulkUserFileNameSlice from './slices/bulkUserFileNameSlice';
import userResultScreenSlice from './slices/bulkUserResultSlice';
import departmentReducer from './slices/clientDetailsSlice';
import clientListSlice from './slices/clientListSlice';
import clientSlice from './slices/clientSlice';
import conciergeReportSlice from './slices/conciergeReportSlice';
import ecaAdhocSearchSlice from './slices/ecaAdhocSearchSlice';
import ecaDocListSlice from './slices/ecaDocListSlice';
import highLightTextSlice from './slices/ecaDocumentHighLightPanelSlice';
import ecaDocumentViewerSlice from './slices/ecaDocumentViewerSlice';
import featureSettingsSlice from './slices/featureSettingsSlice';
import globalChangeTrackerSlice from './slices/globalChangeTrackerSlice';
import highLevelProductionMetricSlice from './slices/highLevelProductionMetricSlice';
import indexingManagementSlice from './slices/indexingManagementSlice';
import indexingSlice from './slices/indexingSlice';
import intakeAuditTabSlice from './slices/intakeAuditTabSlice';
import invoiceModalSlice from './slices/invoiceModalSlice';
import pageHistorySlice from './slices/pageHistorySlice';
import pageViewStateSlice from './slices/pageViewStateSlice';
import permissionGroupSlice from './slices/permissionGroupSlice';
import permissionSlice from './slices/permissionSlice';
import permissionsSlice from './slices/permissionsSlice';
import projectOverviewSlice from './slices/projectOverviewSlice';
import querySlice from './slices/querySlice';
import dialogReducer from './slices/reportsDialogSlice';
import reportsSlice from './slices/reportsSlice';
import roleListSlice from './slices/roleListSlice';
import savedSearchesTemplateSlice from './slices/savedSearchesTemplateSlice';
import selectedClientSlice from './slices/selectedClientSlice';
import strSlice from './slices/strSlice';
import teamMemberSlice from './slices/teamMemberSlice';
import themeReducer from './slices/themeSlice';
import unsavedChangesModalReducer from './slices/unSaveChangesModalSlice';
import roleSlice from './slices/userRoleSlice';
import userReducer, {UserState} from './slices/userSlice';
import workspaceSlice from './slices/workspaceDatasetSlice';
import uploadEvidenceSourceSlice from './slices/uploadEvidenceSourceSlice';
import projectPermissionsSlice from './slices/projectPermissionsSlice';
import welcomeScreenProjectViewSlice from './slices/welcomeScreenProjectViewSlice';
import {createTransform} from 'redux-persist';
import projectCreationSlice from './slices/projectCreationSlice';
import qualtricsSlice from './slices/qualtricsSlice';

// Combine reducers using combineReducers
const rootReducer = combineReducers({
  user: userReducer,
  theme: themeReducer,
  unsavedChangesModal: unsavedChangesModalReducer,
  department: departmentReducer,
  bulkUserFile: bulkUserFileNameSlice,
  teamMember: teamMemberSlice,
  bulkUserEdit: bulkUserEditSlice,
  userResultScreen: userResultScreenSlice,
  askLioSlice: askLioSlice,
  clientList: clientListSlice,
  permissionGroups: permissionGroupSlice,
  clientReducer: clientSlice,
  dialog: dialogReducer,
  permission: permissionSlice,
  permissions: permissionsSlice,
  roleList: roleListSlice,
  workspace: workspaceSlice,
  alerts: alertSlice,
  userRole: roleSlice,
  invoiceModal: invoiceModalSlice,
  selectedClient: selectedClientSlice,
  reports: reportsSlice,
  productionMetric: highLevelProductionMetricSlice,
  conciergeReport: conciergeReportSlice,
  intakeAuditTab: intakeAuditTabSlice,
  globalChangeTracker: globalChangeTrackerSlice,
  pageViewState: pageViewStateSlice,
  indexes: indexingSlice,
  indexesBanner: indexingManagementSlice,
  query: querySlice,
  ecaDocumentViewer: ecaDocumentViewerSlice,
  ecaDocList: ecaDocListSlice,
  ecaHighLightText: highLightTextSlice,
  addExport: addExportSlice,
  STR: strSlice,
  ecaAdhocSearch: ecaAdhocSearchSlice,
  budgetTrackerModal: budgetTrackerModalSlice,
  projectOverview: projectOverviewSlice,
  pageHistory: pageHistorySlice,
  projectCreation: projectCreationSlice,
  savedSearchesTemplate: savedSearchesTemplateSlice,
  analytics: analyticsSlice,
  featureSettings: featureSettingsSlice,
  uploadEvidenceSource: uploadEvidenceSourceSlice,
  projectPermissions: projectPermissionsSlice,
  welcomeScreenProjectView: welcomeScreenProjectViewSlice,
  qualtrics: qualtricsSlice,
});

// Transform to reset userAuthzLoadedAt on rehydrate
const userTransform = createTransform<UserState, UserState>(
  (state) => state, // on save: persist as-is
  (state) => ({...state, userAuthzLoadedAt: null}), // on rehydrate: reset this field
  {whitelist: ['user']}
);

const persistConfig = {
  key: 'root',
  storage,
  blacklist: ['query'],
  transforms: [userTransform],
};

// Created a persisted reducer
const persistedReducer = persistReducer<ReturnType<typeof rootReducer>>(
  persistConfig,
  rootReducer
);

// Persisted reducer here
export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

// Create the persistor
export const persistor = persistStore(store);

// Type definitions
export type RootState = ReturnType<typeof store.getState>;
